// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import * as React from 'react';
import { AudioTrack, useTracks } from '@livekit/components-react';
import { getTrackReferenceId, isLocal } from '@livekit/components-core';
import { Track } from 'livekit-client';
import { useVolume } from '@/context/call-context/use-volume.ts';
import { SpatialAudioTrack } from '@/components/call/spatial-audio-track.tsx';
import { useSettings } from '@/context/settings-context/settings-context.ts';
import { UserPlan } from '@/models';

// import {AudioTrack} from './audio-track';

/** @public */
export interface RoomAudioRendererProps {
  /** Sets the volume for all audio tracks rendered by this component. By default, the range is between `0.0` and `1.0`. */
  volume?: number;
  /**
   * If set to `true`, mutes all audio tracks rendered by the component.
   * @remarks
   * If set to `true`, the server will stop sending audio track data to the client.
   * @alpha
   */
  muted?: boolean;
  plan: UserPlan | null;
}

/**
 * The `AudioRenderer` component is a drop-in solution for adding audio to your LiveKit app.
 * It takes care of handling remote participants’ audio tracks and makes sure that microphones and screen share are audible.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <AudioRenderer />
 * </LiveKitRoom>
 * ```
 * @public
 */
export const AudioRenderer = ({ muted, plan }: RoomAudioRendererProps) => {
  const { getVolume, positions } = useVolume();
  const { spatialAudioEnabled } = useSettings();

  const tracks = useTracks(
    [
      Track.Source.Microphone,
      Track.Source.ScreenShareAudio,
      Track.Source.Unknown,
    ]
  ).filter(
    (ref) =>
      !isLocal(ref.participant) && ref.publication.kind === Track.Kind.Audio
  );

  return (
    <div style={{ display: 'none' }}>
      {tracks.map((trackRef, index) => {
        const participant = trackRef.participant;
        const participantAttributes = participant.attributes;
        const id = participantAttributes['id'];
        const isScreenShare = trackRef.source === Track.Source.ScreenShareAudio;
        const effectiveId = isScreenShare ? `${id}_screenShare` : id;

        const volume = getVolume(effectiveId) ?? 1;
        const position = positions[effectiveId];

        // Include spatialAudioEnabled in key to force complete remount on toggle
        const trackKey = `${getTrackReferenceId(trackRef)}-${spatialAudioEnabled ? 'spatial' : 'standard'}`;

        // If in mobiles don't work - use in pardus only
        return spatialAudioEnabled ? (
          <SpatialAudioTrack
            key={trackKey}
            trackRef={trackRef}
            volume={volume}
            muted={muted}
            position={position}
          />
        ) : (
          <AudioTrack key={trackKey} trackRef={trackRef} volume={volume} muted={muted} />
          // <PlanBasedAudioTrack
          //   key={getTrackReferenceId(trackRef)}
          //   trackRef={trackRef}
          //   volume={volume}
          //   muted={muted}
          //   plan={plan}
          // />
        );
      })}
    </div>
  );
};
